import { BasePageDto } from "@/types/dto/base-page-dto";
import { DiscountDto } from "@/types/dto/discount-dto";
import { EmailDto } from "@/types/dto/email-dto";
import { PartnerHomeDiscountDto } from "@/types/dto/partner-home-discount-dto";
import { PartnerHomeOrderDto } from "@/types/dto/partner-home-order-dto";
import { UserDto } from "@/types/dto/user-dto";
import { FindPartnerEmailPayload } from "@/types/payload/find-partner-email-payload";
import { FindPartnerPasswordPayload } from "@/types/payload/find-partner-password-payload";
import { GetAdminPagePayload } from "@/types/payload/get-admin-page-payload";
import { GetPartnerPagePayload } from "@/types/payload/get-partner-page-payload";
import { SignUpCompanyPartnerPayload } from "@/types/payload/sign-up-company-partner-payload";
import { SignUpPersonalPartnerPayload } from "@/types/payload/sign-up-personal-partner-payload";
import { UpdateAdminPayload } from "@/types/payload/update-admin-payload";
import { UpdatePartnerPasswordPayload } from "@/types/payload/update-partner-password-payload";
import { AxiosInstance } from "axios";
import { AdminHomeItemDto } from "../types/dto/admin-home-item-dto";
import { AdminHomeOrderDto } from "../types/dto/admin-home-order-dto";
import { AdminHomeSalesDto } from "../types/dto/admin-home-sales-dto";
import { BaseResponse } from "../types/dto/base-response";
import { HistoryDto } from "../types/dto/history-dto";
import { PartnerMonthlyStatisticsDto } from "../types/dto/partner-monthly-statistics-dto";
import { CreateAdminPayload } from "../types/payload/create-admin-payload";
import { PartnerIncentiveDiscountsPayload } from "../types/payload/partner-incentive-discounts-payload";
import { UpdatePartnerPayload } from "../types/payload/update-partner-payload";
import { baseApi } from "./base-api";
import { PatchNoteListData } from "@/types/dto/patch-note-list-dto";

enum UserServiceEndpoint {
  FindPartnerEmail = "/user/partner/find-email",
  FindPartnerPassword = "/user/partner/find-password",
  SignUpPersonalPartner = "/user/partner/personal",
  SignUpCompanyPartner = "/user/partner/company",
  CreateAdmin = "/user/admin",
  UpdateAdmin = "/user/admin",
  GetAdminById = "/user/admin",
  GetAdminPage = "/user/admins",
  GetPartnerPage = "/user/partners",
  GetMe = "/user/me",
  GetPartnerById = "/user/partner",
  UpdatePartner = "user/partner",
  UpdatePartnerPassword = "user/partner/password",
  validateEmail = "/user/validate-email",
  GetPartnerMonthlyStatistics = "/user/partner/statistics/monthly",
  GetPartnerHomeOrder = "/user/partner/home/invoice",
  GetPartnerHomeDiscount = "/user/partner/home/discount",
  GetAdminHomeOrder = "/user/admin/home/invoice",
  GetAdminHomeItem = "/user/admin/home/items",
  GetAdminHomeSales = "/user/admin/home/sales",
  GetAdminHomeHistory = "/user/admin/home/patch-notes",
  UpdateCanPayLater = "/user/can-pay-later",
  ChannelTalkMemberHash = "/user/me/member-hash",
}

class UserService {
  private static instance: UserService;
  private _api: AxiosInstance;

  private constructor(api: AxiosInstance) {
    this._api = api;
  }

  static getInstance(apiClient = baseApi): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService(apiClient);
    }
    return UserService.instance;
  }

  async findPartnerEmail(
    payload: FindPartnerEmailPayload
  ): Promise<BaseResponse<EmailDto, FindPartnerEmailPayload>> {
    try {
      const response = await this._api.post<
        BaseResponse<EmailDto, FindPartnerEmailPayload>
      >(UserServiceEndpoint.FindPartnerEmail, {
        businessNumber: payload.businessNumber,
        name: payload.name,
        phone: payload.phone,
      });
      return response.data;
    } catch (error) {
      console.error("Error findPartnerEmail:", error);
      throw error;
    }
  }

  async findPartnerPassword(
    payload: FindPartnerPasswordPayload
  ): Promise<BaseResponse<void, FindPartnerPasswordPayload>> {
    try {
      const response = await this._api.post<
        BaseResponse<void, FindPartnerPasswordPayload>
      >(UserServiceEndpoint.FindPartnerPassword, {
        businessNumber: payload.businessNumber,
        email: payload.email,
        phone: payload.phone,
      });
      return response.data;
    } catch (error) {
      console.error("Error findPartnerPassword:", error);
      throw error;
    }
  }

  async signUpPersonalPartner(
    payload: SignUpPersonalPartnerPayload
  ): Promise<BaseResponse<void, SignUpPersonalPartnerPayload>> {
    try {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        formData.append(key, value as string | Blob);
      });

      const response = await this._api.post<
        BaseResponse<void, SignUpPersonalPartnerPayload>
      >(UserServiceEndpoint.SignUpPersonalPartner, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error signUpPersonalPartner:", error);
      throw error;
    }
  }

  async signUpCompanyPartner(
    payload: SignUpCompanyPartnerPayload
  ): Promise<BaseResponse<void, SignUpCompanyPartnerPayload>> {
    try {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        formData.append(key, value as string | Blob);
      });

      const response = await this._api.post<
        BaseResponse<void, SignUpCompanyPartnerPayload>
      >(UserServiceEndpoint.SignUpCompanyPartner, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error signUpCompanyPartner:", error);
      throw error;
    }
  }

  async updateAdminAsync(
    id: number,
    payload: UpdateAdminPayload
  ): Promise<BaseResponse<void, UpdateAdminPayload>> {
    try {
      const response = await this._api.patch<
        BaseResponse<void, UpdateAdminPayload>
      >(`${UserServiceEndpoint.UpdateAdmin}/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error("Error updateAdminAsync:", error);
      throw error;
    }
  }

  async getAdminById(id: number): Promise<BaseResponse<UserDto, void>> {
    try {
      const response = await this._api.get<BaseResponse<UserDto, void>>(
        `${UserServiceEndpoint.GetAdminById}/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error getAdminById:", error);
      throw error;
    }
  }

  async getAdminPage(
    payload: GetAdminPagePayload
  ): Promise<BaseResponse<BasePageDto<UserDto>, GetPartnerPagePayload>> {
    try {
      const response = await this._api.get<
        BaseResponse<BasePageDto<UserDto>, GetPartnerPagePayload>
      >(UserServiceEndpoint.GetAdminPage, {
        params: {
          page: payload.page,
          size: payload.size,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error getAdmins:", error);
      throw error;
    }
  }

  async getPartnerPage(
    payload: GetPartnerPagePayload
  ): Promise<BaseResponse<BasePageDto<UserDto>, GetPartnerPagePayload>> {
    try {
      const response = await this._api.get<
        BaseResponse<BasePageDto<UserDto>, GetPartnerPagePayload>
      >(UserServiceEndpoint.GetPartnerPage, {
        params: {
          page: payload.page,
          size: payload.size,
          searchNames: payload.searchNames,
          order: payload.order,
          hasIncentiveDiscount: payload.hasIncentiveDiscount,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error getPartners:", error);
      throw error;
    }
  }

  async getMe(): Promise<BaseResponse<UserDto, void>> {
    try {
      const response = await this._api.get<BaseResponse<UserDto, void>>(
        UserServiceEndpoint.GetMe
      );
      return response.data;
    } catch (error) {
      console.error("Error getMe:", error);
      throw error;
    }
  }
  async getChannelTalkMemberHash(): Promise<BaseResponse<string, void>> {
    try {
      const response = await this._api.get<BaseResponse<string, void>>(
        UserServiceEndpoint.ChannelTalkMemberHash
      );
      return response.data;
    } catch (error) {
      console.error("Error getMe:", error);
      throw error;
    }
  }

  async createAdmin(
    payload: CreateAdminPayload
  ): Promise<BaseResponse<UserDto, CreateAdminPayload>> {
    try {
      const response = await this._api.post<
        BaseResponse<UserDto, CreateAdminPayload>
      >(UserServiceEndpoint.CreateAdmin, payload);
      return response.data;
    } catch (error) {
      console.error("Error getMe:", error);
      throw error;
    }
  }

  async updatePartnerAsync(
    id: number,
    payload: UpdatePartnerPayload
  ): Promise<BaseResponse<void, UpdatePartnerPayload>> {
    try {
      const response = await this._api.patch<
        BaseResponse<void, UpdatePartnerPayload>
      >(`${UserServiceEndpoint.UpdatePartner}/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error("Error updatePartnerAsync:", error);
      throw error;
    }
  }

  async getPartnerById(id: number): Promise<BaseResponse<UserDto, void>> {
    try {
      const response = await this._api.get<BaseResponse<UserDto, void>>(
        `${UserServiceEndpoint.GetPartnerById}/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error getPartnerById:", error);
      throw error;
    }
  }

  async updatePartnerPassword(
    payload: UpdatePartnerPasswordPayload
  ): Promise<BaseResponse<void, UpdatePartnerPasswordPayload>> {
    try {
      const response = await this._api.patch<
        BaseResponse<void, UpdatePartnerPasswordPayload>
      >(`${UserServiceEndpoint.UpdatePartnerPassword}`, payload);
      return response.data;
    } catch (error) {
      console.error("Error updatePartnerPassword:", error);
      throw error;
    }
  }

  async validateEmail(email: string): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.get<BaseResponse<void, void>>(
        `${UserServiceEndpoint.validateEmail}?email=${encodeURIComponent(
          email
        )}`
      );
      return response.data;
    } catch (error) {
      console.error("Error validateEmail:", error);
      throw error;
    }
  }

  async getPartnerMonthlyStatistics(): Promise<
    BaseResponse<PartnerMonthlyStatisticsDto, void>
  > {
    try {
      const response = await this._api.get<
        BaseResponse<PartnerMonthlyStatisticsDto, void>
      >(`${UserServiceEndpoint.GetPartnerMonthlyStatistics}`);
      return response.data;
    } catch (error) {
      console.error("Error validateEmail:", error);
      throw error;
    }
  }

  async getPartnerHomeOrder(): Promise<
    BaseResponse<PartnerHomeOrderDto, void>
  > {
    try {
      const response = await this._api.get<
        BaseResponse<PartnerHomeOrderDto, void>
      >(`${UserServiceEndpoint.GetPartnerHomeOrder}`);
      return response.data;
    } catch (error) {
      console.error("Error getPartnerHomeOrder:", error);
      throw error;
    }
  }

  async getPartnerHomeDiscount(): Promise<
    BaseResponse<PartnerHomeDiscountDto, void>
  > {
    try {
      const response = await this._api.get<
        BaseResponse<PartnerHomeDiscountDto, void>
      >(`${UserServiceEndpoint.GetPartnerHomeDiscount}`);
      return response.data;
    } catch (error) {
      console.error("Error getPartnerHomeDiscount:", error);
      throw error;
    }
  }

  async getPartnerPatchNote(
    id: number
  ): Promise<BaseResponse<HistoryDto[], void>> {
    try {
      const response = await this._api.get<BaseResponse<HistoryDto[], void>>(
        `/user/partner/${id}/patch-notes`
      );
      return response.data;
    } catch (error) {
      console.error("Error getPartnerPatchNote:", error);
      throw error;
    }
  }

  async getAdminHomeOrder(
    fromDate: string,
    toDate: string
  ): Promise<BaseResponse<AdminHomeOrderDto, void>> {
    try {
      const response = await this._api.get<
        BaseResponse<AdminHomeOrderDto, void>
      >(`${UserServiceEndpoint.GetAdminHomeOrder}`, {
        params: { fromDate, toDate },
      });
      return response.data;
    } catch (error) {
      console.error("Error getAdminHomeOrder:", error);
      throw error;
    }
  }

  async getAdminHomeItem(): Promise<BaseResponse<AdminHomeItemDto, void>> {
    try {
      const response = await this._api.get<
        BaseResponse<AdminHomeItemDto, void>
      >(`${UserServiceEndpoint.GetAdminHomeItem}`);
      return response.data;
    } catch (error) {
      console.error("Error getAdminHomeItem:", error);
      throw error;
    }
  }

  async getAdminHomeSales(): Promise<BaseResponse<AdminHomeSalesDto[], void>> {
    try {
      const response = await this._api.get<
        BaseResponse<AdminHomeSalesDto[], void>
      >(`${UserServiceEndpoint.GetAdminHomeSales}`);
      return response.data;
    } catch (error) {
      console.error("Error getAdminHomeSales:", error);
      throw error;
    }
  }

  async getAdminHomeHistory(params?: { page: number; size: number }): Promise<PatchNoteListData> {
    try {
      const response = await this._api.get<BaseResponse<PatchNoteListData, void>>(
        UserServiceEndpoint.GetAdminHomeHistory,
        {
          params: {
            page: params?.page || 1,
            size: params?.size || 50
          }
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error getAdminHomeHistory:", error);
      throw error;
    }
  }

  async updateCanPayLater(
    userId: number,
    canPayLater: boolean
  ): Promise<BaseResponse<UserDto, void>> {
    try {
      const response = await this._api.post<BaseResponse<UserDto, void>>(
        `${UserServiceEndpoint.UpdateCanPayLater}`,
        {
          userId,
          canPayLater,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updateCanPayLater:", error);
      throw error;
    }
  }

  async incentiveDiscounts(
    id: number,
    payload: PartnerIncentiveDiscountsPayload
  ): Promise<BaseResponse<DiscountDto[], PartnerIncentiveDiscountsPayload>> {
    try {
      const response = await this._api.post<
        BaseResponse<DiscountDto[], PartnerIncentiveDiscountsPayload>
      >(`/user/partner/${id}/incentive-discounts`, payload);
      return response.data;
    } catch (error) {
      console.error("Error getAdminHomeHistory:", error);
      throw error;
    }
  }

  async countPartnersByGrade(
    gradeId: number
  ): Promise<BaseResponse<{ count: number }, any>> {
    try {
      const response = await this._api.get<
        BaseResponse<{ count: number }, any>
      >(`/user/grade/${gradeId}/count`);
      return response.data;
    } catch (error) {
      console.error("Error countPartnersByGrade:", error);
      throw error;
    }
  }

  async applyDiscountToGrade(
    gradeId: number,
    discounts: {
      discountRate: number;
      fromDate?: string;
      toDate?: string;
      itemIds: number[];
    }[]
  ): Promise<
    BaseResponse<
      {
        totalAffectedPartners: number;
        totalAffectedItems: number;
        results: {
          itemId: number;
          affectedPartners: number;
          success: boolean;
          errorMessage?: string;
        }[];
      },
      any
    >
  > {
    try {
      const response = await this._api.post<
        BaseResponse<
          {
            totalAffectedPartners: number;
            totalAffectedItems: number;
            results: {
              itemId: number;
              affectedPartners: number;
              success: boolean;
              errorMessage?: string;
            }[];
          },
          any
        >
      >(`/user/grade/${gradeId}/apply-discount`, discounts);
      return response.data;
    } catch (error) {
      console.error("Error applyDiscountToGrade:", error);
      throw error;
    }
  }

  async getUserDiscount(
    userId: number
  ): Promise<BaseResponse<DiscountDto[], void>> {
    try {
      const response = await this._api.get<BaseResponse<DiscountDto[], void>>(
        `${UserServiceEndpoint.GetPartnerById}/${userId}/incentive-discounts`
      );
      return response.data;
    } catch (error) {
      console.error("Error getPartnerById:", error);
      throw error;
    }
  }
}

export const userService = UserService.getInstance(baseApi);
